import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { isWidthUp } from "@material-ui/core/withWidth";
import {
  Button,
  FormControlLabel,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  Modal,
  Tooltip,
  TextField,
  Popper,
  List,
  ListItem,
  ListItemText
} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import Autocomplete from "@material-ui/lab/Autocomplete";
import WhisperIcon from '@material-ui/icons/VolumeDown';
import MoreVertIcon from "@material-ui/icons/MoreVert";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import CancelIcon from "@material-ui/icons/Cancel";
import { green } from "@material-ui/core/colors";
import ClearIcon from "@material-ui/icons/Clear";
import MoodIcon from "@material-ui/icons/Mood";
import MicIcon from "@material-ui/icons/Mic";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import LabelOutlinedIcon from "@material-ui/icons/LabelOutlined";
import WarningOutlinedIcon from "@material-ui/icons/WarningOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";

import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";
import MicRecorder from "mic-recorder-to-mp3";
import clsx from "clsx";
import Compressor from "compressorjs";

import { isString, isEmpty, isObject, has } from "lodash";

import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import { cleanupComponent, getSocket, onSocket } from "../../services/socket";
import api from "../../services/api";

import CustomSwitch from "../SwitchCustom/SwitchCustom";
import LinearWithValueLabel from "./ProgressBarCustom";
import RecordingTimer from "./RecordingTimer";
import ScheduleModal from "../ScheduleModal";
import WhatsappTemplatePickerModal from "../WhatsappTemplatePickerModal";
import MessengerTagSendModal from "../MessengerTagSendModal";

import { useLocalStorage } from "../../hooks/useLocalStorage";
import useQuickMessages from "../../hooks/useQuickMessages";

import DocumentTextIcon from "../../assets/icon/DocumentTextIcon";
import PaperClipIcon from "../../assets/icon/PaperClipIcon";
import CalendarIcon from "../../assets/icon/CalendarIcon";
import SuccessIcon from "../../assets/icon/SuccessIcon";
import ErrorIcon from "../../assets/icon/ErrorIcon";
import SendIcon from "../../assets/icon/SendIcon";
import {
  formatServiceWindowExpiry,
  getCloudServiceWindowBannerState,
  isCloudComposerLocked,
  isCloudTicketChannel,
  isWhatsappApiChannel,
  isInstagramDmChannel,
  isMessengerPageChannel,
  serviceWindowI18nPrefix,
} from "../../utils/serviceWindow";

const Mp3Recorder = new MicRecorder({
	bitRate: 128,
	encoder: "application/ogg",
});

const useStyles = makeStyles((theme) => ({
  composerRoot: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
    flexShrink: 0,
    position: "relative",
    overflow: "visible",
    gap: 0,
    backgroundColor: theme.palette.inputBackground,
    borderRadius: 10,
    padding: 4,
    boxSizing: "border-box",
  },

  mainWrapper: {
    background: theme.palette.inputBackground,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    borderTop: `1px solid ${theme.palette.borderTableInput}`,
    border: `1px solid ${theme.palette.borderTableInput}`,
    borderRadius: 10,
    boxShadow: "none",
    minHeight: 60,
    height: "auto",
    width: "100%",
    position: "relative",
    bottom: "auto",
    zIndex: 1,
    overflow: "visible",
    overflowX: "hidden",
    flexShrink: 0,
  },

  mainContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.inputBackground,
    position: "relative",
    overflow: "visible", // Asegurar que el overlay no se corte
    borderRadius: "20px", // Coincidir con el overlay
    minHeight: "auto",
    flex: 1,
    overflowX: "hidden",
  },

  newMessageBox: {
    background: theme.palette.inputBackground,
    width: "100%",
    display: "flex",
    padding: "8px 12px",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column-reverse",
    position: "relative",
    minHeight: "52px",
    maxHeight: "200px",
    flex: 1,
    [theme.breakpoints.down("sm")]: {
      padding: "8px",
      minHeight: "50px",
      maxHeight: "150px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "6px",
      minHeight: "45px",
      maxHeight: "120px",
    },
    "& > .MuiInputBase-root": {
      display: "flex !important",
      width: "100%"
    }
  },

  messageInputWrapper: {
    padding: "4px 8px",
    marginRight: 8,
    flex: 1,
    background: theme.palette.inputMessage,
    fontSize: "1",
    display: "flex",
    alignItems: "center",
    borderRadius: 22,
    width: "100%",
    minHeight: 40,
  },

  messageInput: {
    paddingLeft: 12,
    paddingRight: 12,
    flex: 1,
    width: "100%",
    border: "none",
    fontSize: "15px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "14px",
    },
  },

  placeholderText: {
    position: "absolute",
    left: 10,
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "12px",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    pointerEvents: "none",
  },

  uploadInput: {
    cursor: "pointer",
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    background: theme.palette.total,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },

  emojiBox: {
    position: "fixed",
    bottom: "70px",
    right: "20px",
    marginBottom: "10px",
    width: "auto",
    height: "auto",
    borderTop: "1px solid #e8e8e8",
    zIndex: 1000002,
    borderRadius: "10px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    // Estilos globales para emojis
    "& .emoji-mart-emoji": {
      cursor: "pointer !important",
    },
    "& .emoji-mart-emoji:hover": {
      cursor: "pointer !important",
    },
    "& .emoji-mart-emoji span": {
      cursor: "pointer !important",
    },
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: "8px 12px",
    minHeight: "auto",
    maxHeight: "none",
    flexShrink: 0,
    backgroundColor: theme.palette.inputBackground,
    borderBottom: `1px solid ${theme.palette.borderTableInput}`,
    position: "relative",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      padding: "6px 8px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "4px 6px",
    },
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 8,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "8px",
    display: "flex",
    position: "relative",
    minHeight: "32px", // Reducir altura mínima
    maxHeight: "70px", // Reducir límite de altura para evitar desbordamiento
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      marginRight: 6,
      borderRadius: "6px",
      maxHeight: "60px",
      minHeight: "28px",
    },
    [theme.breakpoints.down("xs")]: {
      marginRight: 4,
      borderRadius: "4px",
      maxHeight: "50px",
      minHeight: "24px",
    },
  },

  replyginMsgBody: {
    padding: "8px 12px",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.4,
    fontSize: "14px",
    [theme.breakpoints.down("sm")]: {
      padding: "6px 10px",
      fontSize: "13px",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "4px 8px",
      fontSize: "12px",
    },
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
    [theme.breakpoints.down("sm")]: {
      borderTopLeftRadius: "6px",
      borderBottomLeftRadius: "6px",
    },
    [theme.breakpoints.down("xs")]: {
      borderTopLeftRadius: "4px",
      borderBottomLeftRadius: "4px",
    },
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
    [theme.breakpoints.down("sm")]: {
      borderTopLeftRadius: "6px",
      borderBottomLeftRadius: "6px",
    },
    [theme.breakpoints.down("xs")]: {
      borderTopLeftRadius: "4px",
      borderBottomLeftRadius: "4px",
    },
  },

  messageContactName: {
    display: "block",
    color: "#6bcbef",
    fontWeight: 600,
    fontSize: "12px",
    marginBottom: "2px",
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      fontSize: "11px",
    },
    [theme.breakpoints.down("xs")]: {
      fontSize: "10px",
    },
  },
  SendButton: {
    background: theme.palette.primary.main,
    height: 45,
    width: 45,
    padding: 0,
    minWidth: "10px !important",
    borderRadius: 22.5,
    marginLeft: 10,
    boxShadow: "none",
    border: "none",
    cursor: "pointer",
    [theme.breakpoints.down("sm")]: {
      height: 40,
      width: 40,
      borderRadius: 20,
    },
    display: "flex !important",
    alignItems: "center",
    justifyContent: "center"
  },

  quickMessagesBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000000,
    backgroundColor: "transparent",
    cursor: "default",
  },
  quickMessagesBox: {
    position: "fixed",
    bottom: "60px",
    marginBottom: "10px",
    width: "auto",
    height: "auto",
    minWidth: "400px",
    maxHeight: "200px",
    overflowY: "auto",
    borderRadius: "10px",
    ...theme.scrollbarStyles,
    background: theme.palette.background.default,
    padding: "10px",
    zIndex: 1000001,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    border: "1px solid rgba(0, 0, 0, 0.1)",
  },
  quickMessageItem: {
    cursor: "pointer",
    padding: "16px 20px", // Aumentado de 8px a 16px vertical y 20px horizontal
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minHeight: "48px", // Altura mínima para asegurar área de click adecuada
    display: "flex",
    alignItems: "center", // Centrar verticalmente el contenido
    borderRadius: "8px", // Añadir bordes redondeados para mejor UX
    margin: "2px 0", // Pequeño margen entre elementos
    transition: "background-color 0.2s ease", // Transición suave para hover
    "&:hover": {
      backgroundColor: theme.palette.action.hover, // Usar color de hover del tema
    },
    "&:active": {
      backgroundColor: theme.palette.action.selected, // Feedback visual al hacer click
    },
  },
  emojiBoxMobile: {
    position: "fixed",
    bottom: "120px", // También posicionado arriba en móvil
    left: "8px",
    right: "8px",
    marginBottom: "10px",
    width: "auto",
    borderTop: "1px solid #e8e8e8",
    borderRadius: "10px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    zIndex: 1000002,
    // Estilos globales para emojis en móvil
    "& .emoji-mart-emoji": {
      cursor: "pointer !important",
    },
    "& .emoji-mart-emoji:hover": {
      cursor: "pointer !important",
    },
    "& .emoji-mart-emoji span": {
      cursor: "pointer !important",
    },
  },
  quickMessagesBoxMobile: { position: "fixed",
    width: "auto",
    height: "auto",
    maxHeight: "200px",
    overflowY: "auto",
    overflowX: "hidden",
    borderRadius: "10px",
    ...theme.scrollbarStyles,
    background: theme.palette.background.default,
    padding: "10px",
    zIndex: 1000001,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    border: "1px solid rgba(0, 0, 0, 0.1)",
  },

  previewModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
  },

  // Preview inline del archivo adjunto (dentro del newMessageBox)
  // Layout horizontal: preview a la izquierda + input a la derecha
  // En mobile, se apila (preview arriba, input abajo)
  inputRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 8,
    width: '100%',
    minHeight: 56,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: 6,
    },
  },
  inputRowWithMedia: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 6,
    minHeight: 0,
  },
  inputRowMain: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
  },
  inlineFilePreview: {
    flex: '0 0 auto',
    maxWidth: 240,
    minWidth: 0,
    animation: '$fadeInUp 200ms ease-out',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      width: '100%',
    },
  },
  inlineFilePreviewExpanded: {
    maxWidth: '100%',
    width: '100%',
  },
  inlineFilePreviewInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    height: '100%',
    minHeight: 56,
    borderRadius: 12,
    backgroundColor: theme.mode === 'light'
      ? 'rgba(241, 176, 0, 0.08)'
      : 'rgba(241, 176, 0, 0.12)',
    border: `1px solid ${theme.mode === 'light' ? 'rgba(241, 176, 0, 0.25)' : 'rgba(241, 176, 0, 0.3)'}`,
    [theme.breakpoints.down('sm')]: {
      minHeight: 48,
    },
  },
  inlineFilePreviewInnerStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
    minHeight: 'auto',
    padding: '8px 10px',
    gap: 0,
  },
  inlineFilePreviewFileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    minWidth: 0,
  },
  inlineFilePreviewMessengerHint: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: 8,
    padding: '7px 8px',
    borderRadius: 8,
    backgroundColor: theme.mode === 'light'
      ? 'rgba(255, 255, 255, 0.72)'
      : 'rgba(0, 0, 0, 0.14)',
    border: `1px solid ${theme.mode === 'light' ? 'rgba(241, 176, 0, 0.18)' : 'rgba(241, 176, 0, 0.22)'}`,
    fontSize: 11,
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
  },
  inlineFilePreviewMessengerHintIcon: {
    fontSize: 15,
    color: theme.palette.primary.main,
    opacity: 0.9,
    flexShrink: 0,
    marginTop: 1,
  },
  inlineFilePreviewImage: {
    width: 36,
    height: 36,
    borderRadius: 6,
    objectFit: 'cover',
    flexShrink: 0,
  },
  inlineFilePreviewDoc: {
    width: 36,
    height: 36,
    borderRadius: 6,
    backgroundColor: theme.mode === 'light' ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  inlineFilePreviewInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    overflow: 'hidden',
  },
  inlineFilePreviewName: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  inlineFilePreviewSize: {
    fontSize: 10,
    color: theme.palette.text.secondary,
  },
  inlineFilePreviewRemove: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
    padding: 4,
  },

  previewModalContent: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '80%',
    maxHeight: '80vh',
    outline: 'none',
    borderRadius: '10px',
    padding: '20px',
    marginTop: '40px',
  },

  previewImage: {
    maxWidth: '100%',
    maxHeight: 'calc(80vh - 120px)',
    objectFit: 'contain',
    borderRadius: '8px',
  },

  previewActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: '20px',
    gap: '10px',
  },

  previewInfo: {
    display: 'flex',
    flexDirection: 'column',
    color: '#fff',
    flex: 1,
  },

  fileName: {
    fontSize: '16px',
    color: '#fff',
    marginBottom: '4px',
    wordBreak: 'break-word',
  },

  fileSize: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.7)',
  },

  actionButtons: {
    display: 'flex',
    gap: '10px',
  },

  sendButton: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
      color: 'rgba(255, 255, 255, 0.3)',
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
  },

  cancelButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },

  progressBar: {
    width: '100%',
    marginTop: '16px',
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '& .MuiLinearProgress-bar': {
      backgroundColor: theme.palette.primary.main,
    },
  },

  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    zIndex: 1,
    '&.Mui-disabled': {
      opacity: 0.5,
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },

  filePreview: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 'calc(100% - 120px)',
  },

  whisperButton: {
    color: theme.palette.text.secondary,
  },

  whisperActiveButton: {
    color: theme.palette.success.main,
    background: 'rgba(76, 175, 80, 0.1)',
    '&:hover': {
      background: 'rgba(76, 175, 80, 0.2)',
    },
  },

  messageActionsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },

  messageActionsButton: {
    color: theme.palette.text.secondary,
  },

  actionIcon: {
    fontSize: 20,
  },

  whisperModeIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    width: '100%',
    borderRadius: 10,
    background: theme.mode === 'light'
      ? '#FFF8E7'
      : `linear-gradient(135deg, ${theme.palette.primary.main}25 0%, ${theme.palette.primary.main}12 100%)`,
    border: theme.mode === 'light'
      ? '1px solid rgba(241, 176, 0, 0.28)'
      : `1px solid ${theme.palette.primary.main}30`,
    color: theme.palette.primary.main,
    position: "relative",
    flexShrink: 0,
    marginBottom: 6,
    boxShadow: `0 1px 3px ${theme.palette.primary.main}10`,
    animation: '$slideDownFade 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  },
  cloudWindowBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    width: '100%',
    borderRadius: 10,
    background: theme.mode === 'light'
      ? 'linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)'
      : 'linear-gradient(135deg, #5d4a1f40 0%, #5d4a1f25 100%)',
    border: '1px solid #ffc10755',
    color: theme.palette.warning.dark,
    position: "relative",
    flexShrink: 0,
    marginBottom: 6,
    boxShadow: '0 1px 3px rgba(255, 193, 7, 0.15)',
    animation: '$slideDownFade 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  },
  cloudWindowBannerOpen: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 12px',
    width: '100%',
    borderRadius: 10,
    background: theme.mode === 'light'
      ? 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)'
      : 'linear-gradient(135deg, #424242 0%, #303030 100%)',
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    position: "relative",
    flexShrink: 0,
    marginBottom: 6,
    boxShadow: theme.mode === 'light'
      ? '0 1px 3px rgba(0, 0, 0, 0.08)'
      : '0 1px 3px rgba(0, 0, 0, 0.25)',
    animation: '$slideDownFade 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
  },
  cloudWindowBannerIconOpen: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.common.white,
    flexShrink: 0,
  },
  cloudWindowBannerIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.common.white,
    flexShrink: 0,
  },
  cloudWindowBannerText: {
    flex: 1,
    minWidth: 0,
  },
  cloudWindowBannerTitle: {
    fontWeight: 600,
    fontSize: '0.875rem',
    lineHeight: 1.3,
  },
  cloudWindowBannerSubtitle: {
    fontSize: '0.8125rem',
    lineHeight: 1.35,
    marginTop: 2,
    opacity: 0.92,
  },
  cloudTemplateButton: {
    flexShrink: 0,
    alignSelf: 'center',
    borderRadius: 8,
    padding: '6px 14px',
    minWidth: 'auto',
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.2,
    textTransform: 'none',
    boxShadow: theme.mode === 'light'
      ? '0 1px 4px rgba(0, 0, 0, 0.14)'
      : '0 1px 4px rgba(0, 0, 0, 0.35)',
    backgroundColor: theme.palette.primary.main,
    color: theme.mode === 'light' ? '#1B1B18' : '#1B1B18',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      filter: 'brightness(0.94)',
      boxShadow: theme.mode === 'light'
        ? '0 2px 6px rgba(0, 0, 0, 0.18)'
        : '0 2px 6px rgba(0, 0, 0, 0.4)',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.primary.main,
      color: '#1B1B18',
      opacity: 0.45,
    },
    [theme.breakpoints.down('xs')]: {
      padding: '6px 10px',
      fontSize: '0.75rem',
    },
  },
  whisperModeIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || '#FFFFFF',
    flexShrink: 0,
  },
  whisperModeText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.25,
  },
  whisperModeTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.primary.main,
    letterSpacing: '0.2px',
  },
  whisperModeSubtitle: {
    fontSize: 11,
    color: theme.mode === 'light' ? '#5f6368' : '#b0b0b0',
    fontWeight: 500,
  },
  whisperModeClose: {
    color: theme.palette.primary.main,
    opacity: 0.7,
    flexShrink: 0,
    padding: 4,
    '&:hover': {
      opacity: 1,
      backgroundColor: `${theme.palette.primary.main}15`,
    },
  },

  actionMenu: {
    padding: theme.spacing(1),
  },
  
  actionMenuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
  },
  
  actionMenuItemIcon: {
    marginRight: theme.spacing(1),
  },

  menuButton: {
    color: theme.palette.text.secondary,
    margin: '0 5px',
  },

  dragOverlay: {
    position: "absolute",
    left: "16px",
    right: "16px",
    top: "16px",
    bottom: "16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999998,
    pointerEvents: "none",
    padding: "32px 24px",
    borderRadius: "16px",
    backgroundColor: theme.mode === "light"
      ? "rgba(241, 176, 0, 0.92)"
      : "rgba(241, 176, 0, 0.88)",
    border: `2px dashed ${theme.palette.primary.contrastText || "#ffffff"}`,
    boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
    backdropFilter: "blur(2px)",
    animation: "$slideUpFade 260ms cubic-bezier(0.2, 0.8, 0.2, 1)",
    transformOrigin: "bottom center",
  },

  dragOverlayText: {
    color: theme.palette.primary.contrastText || "#ffffff",
    fontSize: "20px",
    fontWeight: 800,
    marginTop: "8px",
    textAlign: "center",
    lineHeight: 1.2,
    userSelect: "none",
    pointerEvents: "none",
    letterSpacing: "0.5px",
  },

  dragOverlayIcon: {
    color: theme.palette.primary.contrastText || "#ffffff",
    fontSize: "44px",
    marginBottom: "6px",
    animation: "$bounce 1.2s ease-in-out infinite",
    transform: "translateZ(0)",
  },

  dragOverlaySubtitle: {
    color: theme.palette.primary.contrastText || "#ffffff",
    fontSize: "14px",
    fontWeight: 600,
    marginTop: "4px",
    textAlign: "center",
    lineHeight: 1.4,
    userSelect: "none",
    pointerEvents: "none",
    opacity: 0.95,
  },

  // Estilo para ocultar contenido cuando se está arrastrando
  hiddenWhenDragging: {
    opacity: 0,
    visibility: "hidden",
    transition: "opacity 0.2s ease, visibility 0.2s ease",
  },

  // Estilo para expandir el contenedor cuando se está arrastrando
  expandedForDrag: {
    minHeight: "400px", // Altura mucho más grande
    padding: "20px",
    transition: "min-height 0.3s ease, padding 0.3s ease",
    borderRadius: "20px", // Contenedor principal
    overflow: "hidden", // Oculta scroll para que solo se vea el overlay
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },

  // Animación de pulse para el ícono
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      opacity: 1,
    },
    "50%": {
      transform: "scale(1.1)",
      opacity: 0.8,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },

  // Animación de movimiento lateral para las líneas discontinuas
  "@keyframes dashMove": {
    "0%": {
      backgroundPosition: "0 0, 0 0, 0 100%, 100% 0",
    },
    "100%": {
      backgroundPosition: "12px 0, 0 12px, -12px 100%, 100% -12px",
    },
  },

  // Slide-up + fade-in al entrar el drag (estilo WhatsApp)
  "@keyframes slideUpFade": {
    "0%": {
      opacity: 0,
      transform: "translateY(28px) scale(0.96)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0) scale(1)",
    },
  },

  // Bounce sutil para el ícono
  "@keyframes bounce": {
    "0%, 100%": {
      transform: "translateY(0)",
    },
    "50%": {
      transform: "translateY(-6px)",
    },
  },

  // Fade-in + slide-up sutil al aparecer el preview inline
  "@keyframes fadeInUp": {
    "0%": {
      opacity: 0,
      transform: "translateY(8px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  // Slide-down + fade-in para el banner de whisper
  "@keyframes slideDownFade": {
    "0%": {
      opacity: 0,
      transform: "translateY(-8px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  // Clase adicional para el contenedor cuando hay mensaje de respuesta
  withReplyMessage: {
    minHeight: "auto",
  },

  // Clase para scroll suave cuando el contenido es largo
  scrollContainer: {
    overflowY: "auto",
    scrollBehavior: "smooth",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(0,0,0,0.2)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "rgba(0,0,0,0.3)",
    },
    // Override del scroll cuando se aplica noScroll en drag
    "&$noScroll": {
      overflow: "hidden",
    },
  },

  // Clase que oculta el scroll cuando se arrastra (mayor especificidad por orden)
  noScroll: {},
}));



const QuickMessages = (props) => {
  const {
    disabled,
    showMessage: quickMessageModalOpen,
    setShowMessage: setQuickMessageModalOpen,
    handleAddQuickMessage,
    quickMessages,
    menu,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const handleQuickMessageSelect = (message) => {
    handleAddQuickMessage(message);
    setQuickMessageModalOpen(false); // Cierra el modal después de seleccionar un mensaje rápido
  };

  // Agregar manejador para clicks fuera del componente
  const handleClickOutside = (e) => {
    if (!e.target.closest('.'+classes.quickMessagesBox) && 
        !e.target.closest('[aria-label="quickMessages"]')) {
      setQuickMessageModalOpen(false);
    }
  };

  // Prevenir propagación de eventos de scroll y mouse
  const handleModalEvents = (e) => {
    e.stopPropagation();
  };

  const handleWheelEvent = (e) => {
    e.stopPropagation();
    // Permitir scroll solo dentro del modal
    const target = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = target;
    
    // Si está en el límite superior y se intenta hacer scroll hacia arriba
    if (scrollTop === 0 && e.deltaY < 0) {
      e.preventDefault();
    }
    // Si está en el límite inferior y se intenta hacer scroll hacia abajo
    if (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (quickMessageModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [quickMessageModalOpen]);

  return (
    <>
      <IconButton
        aria-label="quickMessages"
        component="span"
        disabled={disabled}
        onClick={(e) => setQuickMessageModalOpen((prevState) => !prevState)}
        style={isSmallScreen & !menu ? { display: "none" } : {}}
      >
        <DocumentTextIcon />
      </IconButton>
      {quickMessageModalOpen ? (
        <>
          {/* Backdrop para capturar eventos */}
          <div 
            className={classes.quickMessagesBackdrop}
            onMouseDown={handleClickOutside}
            onWheel={handleModalEvents}
            onMouseMove={handleModalEvents}
          />
          <div 
            className={classes.quickMessagesBox}
            onWheel={handleWheelEvent}
            onMouseDown={handleModalEvents}
            onMouseMove={handleModalEvents}
          >
            {quickMessages.map((item) => (
              <div
                key={item.id}
                className={classes.quickMessageItem}
                onClick={() => handleQuickMessageSelect(item.message)}
              >
                {item.shortcode}: {truncateText(item.message, 94)}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </>
  );
};

const SignSwitch = (props) => {
  const { setSignMessage, signMessage, menu, small, marginLeft } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
  return (
    <FormControlLabel
      style={
        isSmallScreen & !menu
          ? { display: "none" }
          : { marginLeft: 10, color: theme.palette.text.secondary }
      }
      label={i18n.t("messagesInput.signMessage")}
      labelPlacement="end"
      control={
        <CustomSwitch
          size={small}
          checked={signMessage}
          onChange={(e) => {
            setSignMessage(e.target.checked);
          }}
          name="showAllTickets"
          color="primary"
          marginTop={-1}
          marginLeft={marginLeft}
        />
      }
    />
  );
};

const truncateText = (text, maxLength) => {
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
};

const FileInput = (props) => {
  const { handleChangeMedias, disableOption, menu } = props;
  const theme = useTheme();
  const classes = useStyles();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const handleFileChange = (e) => {
    if (!e.target.files) {
      return;
    }

    const maxSize = 20 * 1024 * 1024; // 10MB en bytes

    for (let file of Array.from(e.target.files)) {
      if (file.size > maxSize) {
        toastError(`El archivo ${file.name} excede el límite de 20MB permitido`);
        e.target.value = ''; // Limpiar input
        return;
      }
    }

    handleChangeMedias(e);
  };

  return (
    <>
      <input
        multiple
        type="file"
        id="upload-button"
        disabled={disableOption()}
        className={classes.uploadInput}
        onChange={handleFileChange}
      />
      <label htmlFor="upload-button" style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: -10, cursoModal: "pointer" }}>
        <IconButton
          aria-label="upload"
          component="span"
          disabled={disableOption()}
          style={isSmallScreen & !menu ? { display: "none" } : {}}
        >
          <PaperClipIcon />
        </IconButton>
        <span style={{ marginLeft: 10 }}>Adjuntar Archivo</span>
      </label>
    </>
  );
};

const ScheduleEventButton = ({ contactId, menu, handleMenuClose }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [open, setOpen] = useState(false);

  const handleOpenModal = () => {
    setOpen(true);
    if (handleMenuClose) {
      handleMenuClose();
    }
  };

  const handleModalClose = (event, reason) => {
    if (reason === 'backdropClick') {
      setOpen(false);
    }
    setOpen(false); // Asegura que el modal se cierre en todos los casos
  };

  return (
    <>
      <IconButton
        aria-label="schedule-event"
        component="span"
        onClick={handleOpenModal}
        className={classes.scheduleButton}
        style={isSmallScreen && !menu ? { display: "none" } : {fontSize: 15, marginRight: theme.spacing(1)} }
      >
        <CalendarIcon className={classes.icon} />
        {isSmallScreen && <Typography style={{marginLeft: theme.spacing(1.8), color: theme.palette.text.secondary}}>Programar Mensajes</Typography> }  
      </IconButton>

      <ScheduleModal
        open={open}
        onClose={handleModalClose}
        contactId={contactId}
      />
    </>
  );
};

const ActionButtons = (props) => {
  const {
    loading,
    recording,
    ticketStatus,
    composerLocked,
    handleSendMessage,
    handleCancelAudio,
    handleUploadAudio,
    handleStartRecording,
  } = props;
  const classes = useStyles();
  if (recording) {
    return (
      <div className={classes.recorderWrapper}>
        <IconButton
          aria-label="cancelRecording"
          component="span"
          fontSize="large"
          disabled={loading}
          onClick={handleCancelAudio}
        >
          <ErrorIcon
            className={classes.cancelAudioIcon}
            width="24"
            height="24"
          />
        </IconButton>
        {loading ? (
          <div>
            <CircularProgress className={classes.audioLoading} />
          </div>
        ) : (
          <RecordingTimer />
        )}

        <IconButton
          aria-label="sendRecordedAudio"
          component="span"
          onClick={handleUploadAudio}
          disabled={loading}
        >
          <SuccessIcon
            width="24"
            height="24"
            className={classes.sendAudioIcon}
          />
        </IconButton>
      </div>
    );
  } else {
    return (
      <IconButton
        aria-label="showRecorder"
        component="span"
        disabled={loading || ticketStatus !== "open" || composerLocked}
        onClick={handleStartRecording}
      >
        <MicIcon style={{ color: "grey" }} />
      </IconButton>
    );
  }
};

const AttachmentPreview = ({
  classes,
  medias,
  quickMessageMedia,
  previewUrl,
  showMessengerSplitHint,
  messengerSplitHintKey,
  onRemove,
  formatFileSize,
  theme,
}) => {
  if (medias.length === 0 && !quickMessageMedia) {
    return null;
  }

  const isImage = medias.length > 0
    ? medias[0]?.type.startsWith('image/')
    : quickMessageMedia?.type?.startsWith('image');
  const previewSrc = medias.length > 0 ? previewUrl : quickMessageMedia?.url;
  const displayName = medias.length > 0 ? medias[0]?.name : quickMessageMedia?.name;

  return (
    <div
      className={clsx(
        classes.inlineFilePreview,
        showMessengerSplitHint && classes.inlineFilePreviewExpanded,
      )}
    >
      <div
        className={clsx(
          classes.inlineFilePreviewInner,
          showMessengerSplitHint && classes.inlineFilePreviewInnerStacked,
        )}
      >
        <div className={classes.inlineFilePreviewFileRow}>
          {isImage ? (
            <img
              src={previewSrc}
              alt="Preview"
              className={classes.inlineFilePreviewImage}
            />
          ) : (
            <div className={classes.inlineFilePreviewDoc}>
              <DocumentTextIcon
                style={{
                  fontSize: 22,
                  color: theme.palette.primary.main,
                }}
              />
            </div>
          )}
          <div className={classes.inlineFilePreviewInfo}>
            <Typography
              className={classes.inlineFilePreviewName}
              noWrap
              title={displayName}
            >
              {displayName}
            </Typography>
            <Typography className={classes.inlineFilePreviewSize}>
              {medias.length > 0
                ? formatFileSize(medias[0]?.size || 0)
                : 'Imagen del mensaje rápido'}
            </Typography>
          </div>
          <IconButton
            size="small"
            className={classes.inlineFilePreviewRemove}
            onClick={onRemove}
            aria-label="Quitar archivo"
          >
            <CancelIcon fontSize="small" />
          </IconButton>
        </div>
        {showMessengerSplitHint ? (
          <div className={classes.inlineFilePreviewMessengerHint}>
            <InfoOutlinedIcon className={classes.inlineFilePreviewMessengerHintIcon} />
            <span>{i18n.t(messengerSplitHintKey)}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ActionIcons = ({ loading, disableOption, handleOpenEmojiModal, handleMenuOpen }) => {
  const classes = useStyles();

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title="Emoji">
        <IconButton
          aria-label="showEmoji"
          component="span"
          disabled={loading || disableOption()}
          onClick={handleOpenEmojiModal}
          className={classes.messageActionsButton}
        >
          <MoodIcon className={classes.actionIcon} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Opciones">
        <IconButton
          onClick={handleMenuOpen}
          className={classes.menuButton}
          disabled={loading || disableOption()}
        >
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const CustomInput = React.memo(({
  loading,
  inputRef,
  ticketStatus,
  inputMessage,
  setInputMessage,
  handleSendMessage,
  handleInputPaste,
  disableOption,
  quickMessages,
  handleAddQuickMessage: handleAddQuickMessageProp
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const itemRefs = useRef([]);

  const filteredOptions = useMemo(() => {
    if (inputMessage && inputMessage.startsWith("/")) {
      const query = inputMessage.slice(1).toLowerCase();

      if (query.length === 0) return quickMessages;

      return quickMessages.filter((m) =>
        m.label.toLowerCase().includes(query)
      );
    }
    return [];
  }, [inputMessage, quickMessages]);

  useEffect(() => {
    setPopupOpen(inputMessage?.startsWith("/"));
  }, [inputMessage]);
  useEffect(() => {
    setHighlightIndex(0);
  }, [filteredOptions]);
  useEffect(() => {
    itemRefs.current = [];
  }, [filteredOptions]);
  useEffect(() => {
    const el = itemRefs.current[highlightIndex];
    if (el) {
      el.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightIndex]);

  const onKeyDown = useCallback((e) => {
    if (popupOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        const option = filteredOptions[highlightIndex];
        if (option) {
          handleSelectOption(option);
        }
        return;
      }
    }

    // comportamiento normal
    if (!e.shiftKey && e.key === "Enter") {
      e.preventDefault();
      handleSendMessage();
    }
  }, [popupOpen, filteredOptions, highlightIndex, handleSendMessage]);

  const onPaste = useCallback((e) => {
    if (ticketStatus === "open") {
      handleInputPaste(e);
    }
  }, [ticketStatus, handleInputPaste]);

  const setInputRef = useCallback((input) => {
    if (input) {
      input.focus();
      inputRef.current = input;
      setAnchorEl(input);
    }
  }, [inputRef]);

  const handleSelectOption = (option) => {
    // Si tenemos la prop handleAddQuickMessage y la opcion trae info
    // completa del quick message, delegamos para que tambien se
    // procese la imagen adjunta (si existe).
    if (typeof handleAddQuickMessageProp === "function" && option && option.id) {
      handleAddQuickMessageProp(option);
    } else {
      setInputMessage(option.value);
    }

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.scrollTop = inputRef.current.scrollHeight;
      }
    }, 200);

    setPopupOpen(false);
  };

  return (
    <div className={classes.messageInputWrapper} style={{ flex: 1, width: "100%" }}>
      <TextField
        disabled={disableOption()}
        inputRef={setInputRef}
        multiline
        maxRows={5}
        className={classes.messageInput}
        value={inputMessage}
        onChange={(e) => setInputMessage(e.target.value)}
        placeholder="Escribe un mensaje..."
        fullWidth
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        InputProps={{
          disableUnderline: true,
        }}
      />
      <Popper
        open={popupOpen}
        anchorEl={anchorEl}
        placement="top-start"
        style={{
          zIndex: 1300,
          maxHeight: '40vh',
          overflowY: "auto",
          width: anchorEl?.clientWidth ? anchorEl.clientWidth : 'auto',
        }}
      >
        <Paper elevation={3}>
          <List>
            {filteredOptions.map((option, index) => (
              <ListItem
                button
                key={option.id}
                selected={index === highlightIndex}
                ref={(el) => (itemRefs.current[index] = el)}
                onClick={() => handleSelectOption(option)}
              >
                <ListItemText primary={option.label} />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Popper>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.loading === nextProps.loading &&
    prevProps.ticketStatus === nextProps.ticketStatus &&
    prevProps.inputMessage === nextProps.inputMessage &&
    prevProps.disableOption === nextProps.disableOption &&
    prevProps.quickMessages === nextProps.quickMessages
  );
});

// ✅ ELIMINADO: Hook innecesario que causaba delay en la barra
// Ya no necesitamos llamar a /tickets/{ticketId} porque ticketStatus viene como prop

const MessageInputCustom = (props) => {
  const { ticketStatus, ticketId, contactId, whatsappId, currentUser, quickMessageModalOpen, setQuickMessageModalOpen, emojiModalOpen, setEmojiModalOpen, channel, serviceWindowExpiresAt, onOptimisticMessage } = props;
  const classes = useStyles();
  const [medias, setMedias] = useState([]);
  const [quickMessageMedia, setQuickMessageMedia] = useState(null);
  const [inputMessage, setInputMessage] = useState("");
  // Estado showMessage ahora viene de props como quickMessageModalOpen
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [percentLoading, setPercentLoading] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [quickMessagesData, setQuickMessagesData] = useState([]);
  const [whisperMode, setWhisperMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [messengerTagModalOpen, setMessengerTagModalOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const { list: listQuickMessages } = useQuickMessages();
  
  const componentId = useMemo(() => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `messageInputCustom-${timestamp}-${random}`;
  }, []);

  const inputRef = useRef();
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);
  const [windowExpiryTick, setWindowExpiryTick] = useState(0);

  useEffect(() => {
    if (!isCloudTicketChannel(channel)) {
      return undefined;
    }

    const interval = setInterval(() => {
      setWindowExpiryTick((value) => value + 1);
    }, 60_000);

    let expiryTimer;
    if (serviceWindowExpiresAt) {
      const expiresAt = new Date(serviceWindowExpiresAt).getTime();
      if (!Number.isNaN(expiresAt)) {
        const delay = expiresAt - Date.now();
        if (delay > 0) {
          expiryTimer = setTimeout(() => {
            setWindowExpiryTick((value) => value + 1);
          }, delay + 250);
        }
      }
    }

    return () => {
      clearInterval(interval);
      if (expiryTimer) {
        clearTimeout(expiryTimer);
      }
    };
  }, [channel, serviceWindowExpiresAt]);

  const cloudComposerLocked = useMemo(
    () => isCloudComposerLocked(channel, serviceWindowExpiresAt),
    [channel, serviceWindowExpiresAt, windowExpiryTick],
  );

  const handleTemplateClick = useCallback(() => {
    if (!whatsappId) {
      toast.info(i18n.t("whatsappTemplates.picker.noLine"));
      return;
    }
    setTemplatePickerOpen(true);
  }, [whatsappId]);

  const renderCloudServiceWindowBanner = () => {
    const bannerState = getCloudServiceWindowBannerState(
      channel,
      serviceWindowExpiresAt,
    );
    if (bannerState === "hidden") {
      return null;
    }

    const i18nPrefix = serviceWindowI18nPrefix(channel);
    const expiryLabel = formatServiceWindowExpiry(serviceWindowExpiresAt);
    const showTemplateAction = isWhatsappApiChannel(channel);
    const showMessengerTagAction =
      (isMessengerPageChannel(channel) || isInstagramDmChannel(channel)) &&
      bannerState !== "open";

    if (bannerState === "open") {
      return (
        <div
          className={classes.cloudWindowBannerOpen}
          role="status"
          data-testid="cloud-service-window-banner-open"
        >
          <div className={classes.cloudWindowBannerIconOpen}>
            <WarningOutlinedIcon style={{ fontSize: 16 }} />
          </div>
          <div className={classes.cloudWindowBannerText}>
            <Typography className={classes.cloudWindowBannerSubtitle}>
              {i18n.t(`${i18nPrefix}.bannerBodyOpen`, {
                expiry: expiryLabel ?? "",
              })}
            </Typography>
          </div>
        </div>
      );
    }

    const body =
      bannerState === "no_inbound"
        ? i18n.t(`${i18nPrefix}.bannerBodyNoInbound`)
        : i18n.t(`${i18nPrefix}.bannerBodyExpired`, {
            expiry: expiryLabel ? ` (${expiryLabel})` : "",
          });

    return (
      <div className={classes.cloudWindowBanner} role="status" data-testid="cloud-service-window-banner">
        <div className={classes.cloudWindowBannerIcon}>
          <DescriptionOutlinedIcon style={{ fontSize: 16 }} />
        </div>
        <div className={classes.cloudWindowBannerText}>
          <Typography className={classes.cloudWindowBannerTitle}>
            {i18n.t(`${i18nPrefix}.bannerTitle`)}
          </Typography>
          <Typography className={classes.cloudWindowBannerSubtitle}>
            {body}
          </Typography>
        </div>
        {showTemplateAction ? (
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<DescriptionOutlinedIcon style={{ fontSize: 16 }} />}
            aria-label="sendTemplate"
            disabled={loading || ticketStatus !== "open"}
            onClick={handleTemplateClick}
            className={classes.cloudTemplateButton}
          >
            {i18n.t("ticketComposer.cloudWindow.templateButton")}
          </Button>
        ) : null}
        {showMessengerTagAction ? (
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<LabelOutlinedIcon style={{ fontSize: 16 }} />}
            aria-label="sendMessengerTag"
            disabled={loading || ticketStatus !== "open"}
            onClick={() => setMessengerTagModalOpen(true)}
            className={classes.cloudTemplateButton}
          >
            {i18n.t("messengerTags.modal.openButton")}
          </Button>
        ) : null}
      </div>
    );
  };

  const renderWhisperModeBanner = () => {
    if (!whisperMode) {
      return null;
    }

    return (
      <div className={classes.whisperModeIndicator}>
        <div className={classes.whisperModeIcon}>
          <WhisperIcon style={{ fontSize: 16 }} />
        </div>
        <div className={classes.whisperModeText}>
          <Typography className={classes.whisperModeTitle}>
            Mensaje privado
          </Typography>
          <Typography className={classes.whisperModeSubtitle}>
            No se enviará a WhatsApp, solo quedará en el chat
          </Typography>
        </div>
        <IconButton
          size="small"
          className={classes.whisperModeClose}
          onClick={() => setWhisperMode(false)}
          aria-label="Desactivar modo privado"
        >
          <CancelIcon style={{ fontSize: 16 }} />
        </IconButton>
      </div>
    );
  };

  const renderComposerChrome = () => (
    <>
      {renderCloudServiceWindowBanner()}
      {renderWhisperModeBanner()}
    </>
  );

  const renderCloudTemplateMenuItem = () => {
    if (!isWhatsappApiChannel(channel)) {
      return null;
    }

    return (
      <MenuItem
        onClick={() => {
          handleTemplateClick();
          handleMenuClose();
        }}
        disabled={loading || ticketStatus !== "open"}
      >
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginLeft: '12px' }}>
          <DescriptionOutlinedIcon />
          <span style={{ marginLeft: '22px' }}>
            {i18n.t("ticketComposer.cloudWindow.templateMenuItem")}
          </span>
        </div>
      </MenuItem>
    );
  };

  const memoizedQuickMessages = useMemo(() => {
    return quickMessagesData.map((m) => {
      let truncatedMessage = m.message;
      if (isString(truncatedMessage) && truncatedMessage.length > 35) {
        truncatedMessage = m.message.substring(0, 35) + "...";
      }
      return {
        value: m.message,
        label: `/${m.shortcode} - ${truncatedMessage}`,
        // Pasamos la info completa del quick message para que CustomInput
        // pueda llamar a handleAddQuickMessage con todo el contexto
        // (incluyendo mediaUrl, mediaType, mediaName, mimeType).
        id: m.id,
        shortcode: m.shortcode,
        message: m.message,
        mediaUrl: m.mediaUrl || null,
        mediaType: m.mediaType || null,
        mediaName: m.mediaName || null,
        mimeType: m.mimeType || null,
      };
    });
  }, [quickMessagesData]);

  useEffect(() => {
    const fetchQuickMessages = async () => {
      try {
        const companyId = localStorage.getItem("companyId");
        const messages = await listQuickMessages({
          companyId,
          userId: currentUser.id,
        });
        setQuickMessagesData(messages);
      } catch (error) {
        console.log("Error al obtener la informacion del usuario");
        setQuickMessagesData([]);
      }
    };
    fetchQuickMessages();
  }, [currentUser.id]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [replyingMessage]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setEmojiModalOpen(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = getSocket(companyId);

    const handleMessage = (data) => {
      if (data.message && data.action === "create") {
        if (data.message.ticketId === ticketId) {
          // Solo actualizar el foco si es necesario
          if (data.message.fromMe) {
            inputRef.current?.focus();
          }
        }
      }
    };

    onSocket(`company:message`, handleMessage, componentId)

    return () => {
      cleanupComponent(componentId);
      console.log(`🧹 ${componentId} - Cleanup con cleanupComponent`);
    };
  }, [ticketId, componentId]);

  const handleMenuOpen = (event) => {
    setShowEmoji(false);
    setEmojiModalOpen(false);
    setQuickMessageModalOpen(false);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddEmoji = useCallback((e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);
  }, []);

  const handleAddQuickMessage = useCallback((item) => {
    setInputMessage(item.message);
    setQuickMessageModalOpen(false);
    handleMenuClose();

    // Si el quick message tiene una imagen adjunta, mostramos la preview
    // usando la URL del backend directamente (sin fetch, para evitar CORS).
    // Guardamos la referencia en un state auxiliar para distinguirla de
    // archivos locales (los archivos del explorador requieren FormData al
    // enviar; los quick messages ya están en el backend y se envían como
    // referencia en el body JSON).
    if (item.mediaUrl && item.mediaType && item.mediaType.startsWith('image')) {
      setQuickMessageMedia({
        url: item.mediaUrl,
        name: item.mediaName || 'quick-message-image.jpg',
        type: item.mediaType,
        mimeType: item.mimeType || item.mediaType || 'image/jpeg',
      });
      setMedias([]);
      setPreviewUrl(null);
    } else {
      setQuickMessageMedia(null);
      setMedias([]);
      setPreviewUrl(null);
    }
  }, []);

  const handleOpenEmojiModal = () => {
    const newShowEmoji = !showEmoji;
    setShowEmoji(newShowEmoji);
    setEmojiModalOpen(newShowEmoji);
    setQuickMessageModalOpen(false);
    handleMenuClose();
  };

  const handleOpenQuickMessageModal = () => {
    setQuickMessageModalOpen(true);
    setShowEmoji(false);
    setEmojiModalOpen(false);
    handleMenuClose();
  };

  const handleChangeMedias = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño máximo (20MB)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toastError("El archivo excede el límite de 20MB permitido");
      e.target.value = null;
      return;
    }

    setMedias([file]);

    // Crear preview para imágenes (se muestra inline, no en modal)
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }

    handleMenuClose();
    e.target.value = null;
  };

  // Funciones para drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDragCounter(prev => {
      const newCount = prev - 1;
      // Resetear el estado si el contador llega a 0 o menos
      if (newCount <= 0) {
        setIsDragOver(false);
        return 0;
      }
      return newCount;
    });
  };

  // Hook para controlar el overflow del body cuando el modal de respuestas rápidas está abierto
  useEffect(() => {
    if (quickMessageModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup al desmontar el componente
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [quickMessageModalOpen]);

  // Hook para resetear drag state cuando se sale completamente del área
  useEffect(() => {
    const handleGlobalDragEnd = (e) => {
      // Siempre resetear cuando termina el drag
      setIsDragOver(false);
      setDragCounter(0);
    };

    const handleGlobalDragLeave = (e) => {
      // Verificar si realmente salió del área del componente
      if (e.clientX === 0 && e.clientY === 0) {
        setIsDragOver(false);
        setDragCounter(0);
      }
    };

    // Timeout de seguridad para resetear el estado
    let dragTimeout;
    const handleDragStart = () => {
      clearTimeout(dragTimeout);
    };

    const handleDragEndWithDelay = () => {
      dragTimeout = setTimeout(() => {
        setIsDragOver(false);
        setDragCounter(0);
      }, 100);
    };

    document.addEventListener('dragend', handleGlobalDragEnd);
    document.addEventListener('dragleave', handleGlobalDragLeave);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('dragover', handleDragEndWithDelay);
    
    return () => {
      document.removeEventListener('dragend', handleGlobalDragEnd);
      document.removeEventListener('dragleave', handleGlobalDragLeave);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('dragover', handleDragEndWithDelay);
      clearTimeout(dragTimeout);
    };
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Resetear inmediatamente el estado de drag
    setIsDragOver(false);
    setDragCounter(0);

    if (disableOption()) {
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Validar tamaño máximo (20MB)
      const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        toastError("El archivo excede el límite de 20MB permitido");
        return;
      }

      setMedias([file]);
      
      // Crear preview para imágenes
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const hasAttachmentPreview = medias.length > 0 || !!quickMessageMedia;
  const canSendComposer =
    Boolean(inputMessage.trim()) || medias.length > 0 || !!quickMessageMedia;
  const showMessengerSplitHint =
    (isMessengerPageChannel(channel) || isInstagramDmChannel(channel)) &&
    medias.length > 0;
  const splitHintPrefix = serviceWindowI18nPrefix(channel);
  const messengerSplitHintKey = inputMessage.trim()
    ? `${splitHintPrefix}.splitMediaTextHint`
    : `${splitHintPrefix}.splitMediaTextHintPending`;

  const handleRemoveAttachment = () => {
    setMedias([]);
    setPreviewUrl(null);
    setQuickMessageMedia(null);
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0) {
      const file = e.clipboardData.files[0];
      
        const maxSize = 20 * 1024 * 1024;
      if (file.size > maxSize) {
        toastError("El archivo excede el límite de 20MB permitido");
        return;
      }

      setMedias([file]);
      
      // Crear preview para imágenes
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleUploadMedia = async (file, captionBody = "") => {
    try {
      const formData = new FormData();

      const tempId = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      const trimmedCaption = captionBody.trim();
      const splitMessengerMediaText =
        (isMessengerPageChannel(channel) || isInstagramDmChannel(channel)) &&
        Boolean(trimmedCaption);

      if (typeof onOptimisticMessage === "function") {
        try {
          if (splitMessengerMediaText) {
            onOptimisticMessage({
              id: tempId,
              ticketId,
              body: "",
              fromMe: true,
              mediaUrl: "",
              mediaType: file.type || null,
              mediaName: file.name || null,
              isWhisper: !!whisperMode,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              ack: 0,
              status: "pending",
              _optimistic: true,
              tempId,
            });
            onOptimisticMessage({
              id: `${tempId}-text`,
              ticketId,
              body: trimmedCaption,
              fromMe: true,
              mediaUrl: "",
              mediaType: null,
              mediaName: null,
              isWhisper: !!whisperMode,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              ack: 0,
              status: "pending",
              _optimistic: true,
              tempId: `${tempId}-text`,
            });
          } else {
            onOptimisticMessage({
              id: tempId,
              ticketId,
              body: trimmedCaption,
              fromMe: true,
              mediaUrl: "",
              mediaType: file.type || null,
              mediaName: file.name || null,
              isWhisper: !!whisperMode,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              ack: 0,
              status: "pending",
              _optimistic: true,
              tempId,
            });
          }
        } catch (e) {
          console.warn("onOptimisticMessage lanzó excepción:", e);
        }
      }

      formData.append("body", trimmedCaption);
      formData.append("fromMe", true);
      formData.append("isWhisper", whisperMode);
      formData.append("tempId", tempId);

      if (file.type.startsWith('image/')) {
        const compressedFile = await new Promise((resolve, reject) => {
          new Compressor(file, {
            quality: 0.6,
            success: (compressedFile) => resolve(compressedFile),
            error: (err) => reject(err),
          });
        });

        formData.append("medias", compressedFile);

        await api.post(`/messages/${ticketId}`, formData, {
          onUploadProgress: (progressEvent) => {
            const currentProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setPercentLoading(currentProgress);
          },
        });
      } else {
        formData.append("medias", file);

        await api.post(`/messages/${ticketId}`, formData, {
          onUploadProgress: (progressEvent) => {
            const currentProgress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setPercentLoading(currentProgress);
          },
        });
      }
    } catch (err) {
      console.error(err);
      toastError(`Error al procesar archivo: ${err.message || 'Error desconocido'}`);
      throw err;
    }
  };

  const handleSendMessage = useCallback(async () => {
    if (cloudComposerLocked) return;
    if (inputMessage.trim() === "" && medias.length === 0 && !quickMessageMedia) return;

    const currentMessage = inputMessage;
    const currentMedias = [...medias];
    const currentQuickMedia = quickMessageMedia;
    setInputMessage("");
    setMedias([]);
    setPreviewUrl(null);
    setQuickMessageMedia(null);
    setShowEmoji(false);
    setEmojiModalOpen(false);
    setReplyingMessage(null);

    const tempId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    const file = currentMedias[0];
    const hasMedia = !!file;
    const hasQuickMedia = !!currentQuickMedia;
    const splitMessengerMediaText =
      (isMessengerPageChannel(channel) || isInstagramDmChannel(channel)) &&
      hasMedia &&
      currentMessage.trim().length > 0;

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: hasQuickMedia ? currentQuickMedia.url : "",
      mediaType: hasQuickMedia ? currentQuickMedia.type : (hasMedia ? file.type : null),
      mediaName: hasQuickMedia ? currentQuickMedia.name : (hasMedia ? file.name : null),
      mimeType: hasQuickMedia
        ? currentQuickMedia.mimeType
        : (hasMedia ? file.type : null),
      body: signMessage
        ? `*${user?.name}:*\n${currentMessage.trim()}`
        : currentMessage.trim(),
      quotedMsgId: replyingMessage?.id,
      isWhisper: whisperMode,
      tempId,
    };

    if (typeof onOptimisticMessage === "function") {
      try {
        if (splitMessengerMediaText) {
          onOptimisticMessage({
            id: tempId,
            ticketId,
            body: "",
            fromMe: true,
            mediaUrl: message.mediaUrl,
            mediaType: message.mediaType,
            mediaName: message.mediaName,
            mimeType: message.mimeType,
            quotedMsgId: null,
            isWhisper: !!whisperMode,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            ack: 0,
            status: "pending",
            _optimistic: true,
            tempId,
          });
          onOptimisticMessage({
            id: `${tempId}-text`,
            ticketId,
            body: message.body,
            fromMe: true,
            mediaUrl: "",
            mediaType: null,
            mediaName: null,
            mimeType: null,
            quotedMsgId: message.quotedMsgId || null,
            isWhisper: !!whisperMode,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            ack: 0,
            status: "pending",
            _optimistic: true,
            tempId: `${tempId}-text`,
          });
        } else {
          onOptimisticMessage({
            id: tempId,
            ticketId,
            body: message.body,
            fromMe: true,
            mediaUrl: message.mediaUrl,
            mediaType: message.mediaType,
            mediaName: message.mediaName,
            mimeType: message.mimeType,
            quotedMsgId: message.quotedMsgId || null,
            isWhisper: !!whisperMode,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            ack: 0,
            status: "pending",
            _optimistic: true,
            tempId,
          });
        }
      } catch (e) {
        console.warn("onOptimisticMessage lanzó excepción:", e);
      }
    }

    try {
      if (hasMedia) {
        const formData = new FormData();
        formData.append("body", message.body || "");
        formData.append("fromMe", "true");
        if (replyingMessage?.id) formData.append("quotedMsgId", replyingMessage.id);
        if (whisperMode) formData.append("isWhisper", "true");
        formData.append("tempId", tempId);
        formData.append("medias", file);
        await api.post(`/messages/${ticketId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // Para texto solo o quick message con imagen (la URL ya esta en el backend)
        await api.post(`/messages/${ticketId}`, message);
      }
    } catch (err) {
      setInputMessage(currentMessage);
      setMedias(currentMedias);
      if (currentQuickMedia) setQuickMessageMedia(currentQuickMedia);
      if (hasMedia && file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => setPreviewUrl(reader.result);
        reader.readAsDataURL(file);
      }
      toastError(err);
    }
  }, [inputMessage, signMessage, user?.name, replyingMessage, ticketId, whisperMode, onOptimisticMessage, medias, quickMessageMedia, cloudComposerLocked, channel]);

  const handleStartRecording = async () => {
    if (cloudComposerLocked) return;
    setLoading(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await Mp3Recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleUploadAudio = async () => {
    if (cloudComposerLocked) return;
    setLoading(true);
    try {
      const [, blob] = await Mp3Recorder.stop().getMp3();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `audio-record-site-${new Date().getTime()}.ogg`;
      const audioBlob = new Blob([blob], { type: "audio/ogg; codecs=opus" });

      const tempId = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      if (typeof onOptimisticMessage === "function") {
        try {
          onOptimisticMessage({
            id: tempId,
            ticketId,
            body: "",
            fromMe: true,
            mediaUrl: "",
            mediaType: "audio/ogg; codecs=opus",
            mediaName: filename,
            isWhisper: !!whisperMode,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            ack: 0,
            status: "pending",
            _optimistic: true,
            tempId,
          });
        } catch (e) {
          console.warn("onOptimisticMessage lanzó excepción:", e);
        }
      }

      formData.append("medias", audioBlob, filename);
      formData.append("body", ''); // No se envía body para audio
      formData.append("fromMe", true);
      formData.append("mimetype", "audio/ogg; codecs=opus");
      formData.append("tempId", tempId);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      await Mp3Recorder.stop().getMp3();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const disableOption = () => {
    return loading || recording || ticketStatus !== "open" || cloudComposerLocked;
  };

  const renderReplyingMessage = (message) => {
    // Función para truncar el mensaje si es muy largo
    const truncateMessage = (text, maxLength = 150) => {
      if (!text) return "";
      return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
    };

    // Autor del mensaje (mostrar también cuando es propio)
    const authorName = message.fromMe
      ? (user?.name || i18n.t("messagesList.you") || "Tú")
      : (message.contact?.name || "Contacto");

    // Construir preview cuando el body está vacío (p. ej., imágenes/documentos sin caption)
    const buildMediaPreview = () => {
      const type = (message.mediaType || "").toLowerCase();
      const typeLabel =
        type.includes("image") ? "Imagen" :
        type.includes("video") ? "Video" :
        type.includes("audio") ? "Audio" :
        type ? "Archivo" : "";

      // Intentar obtener nombre de archivo desde body o mediaUrl
      let fileName = "";
      if (message.body && message.body.trim().length > 0) {
        fileName = message.body.trim();
      } else if (message.mediaUrl) {
        try {
          const url = new URL(message.mediaUrl);
          fileName = decodeURIComponent(url.pathname.split("/").pop() || "");
        } catch (_) {
          // Si no es una URL válida, tomar el último segmento manualmente
          const parts = String(message.mediaUrl).split("/");
          fileName = decodeURIComponent(parts[parts.length - 1] || "");
        }
      }

      const label = [typeLabel, fileName].filter(Boolean).join(": ");
      return label || "Archivo";
    };

    const previewText = (() => {
      if (message && typeof message.body === "string" && message.body.trim().length > 0) {
        return truncateMessage(message.body);
      }
      // Si no hay body, pero es un mensaje con media, armar descripción
      if (message && (message.mediaUrl || message.mediaType)) {
        return buildMediaPreview();
      }
      return "";
    })();

    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            <span className={classes.messageContactName}>
              {authorName}
            </span>
            <div style={{ 
              flex: 1, 
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              textOverflow: "ellipsis"
            }}>
              {previewText}
            </div>
          </div>
        </div>
        <IconButton
          aria-label="clearReply"
          component="span"
          disabled={loading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
          style={{
            padding: "4px",
            alignSelf: "flex-start",
            marginTop: "2px"
          }}
          size="small"
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      </div>
    );
  };

  // ✅ OPTIMIZADO: Usar directamente ticketStatus de props en lugar de hacer llamada a API

  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewUrl(null);
    setUploadingMedia(false);
  };

  const getEmojiPerLine = () => {
    const width = props.width || 'md';
    if (isWidthUp('lg', width)) {
      return 12;
    } else if (isWidthUp('md', width)) {
      return 9;
    } else {
      return 7;
    }
  };

  const renderMainComponent = () => {
    if (medias.length > 0) {
      return (
        <div className={classes.composerRoot}>
          {renderComposerChrome()}

          <Paper 
            variant="outlined" 
            className={clsx(classes.mainWrapper, {
              [classes.withReplyMessage]: !!replyingMessage
            })}
          >
            {replyingMessage && renderReplyingMessage(replyingMessage)}
            
            <Paper
              square
              elevation={0}
              className={clsx(classes.mainContainer, classes.scrollContainer, {
                [classes.mainContainerClosed]: disableOption(),
                [classes.expandedForDrag]: isDragOver,
                [classes.noScroll]: isDragOver
              })}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {/* Overlay de drag and drop */}
              {isDragOver && (
                <div className={classes.dragOverlay}>
                  <AttachFileIcon className={classes.dragOverlayIcon} />
                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <Typography className={classes.dragOverlayText}>
                      CARGAR ARCHIVOS
                    </Typography>
                    <Typography className={classes.dragOverlaySubtitle}>
                      Suelta tu archivo aquí
                    </Typography>
                  </div>
                </div>
              )}
            
{ticketStatus === "open" && (
                 <div className={clsx(classes.newMessageBox, {
                   [classes.expandedForDrag]: isDragOver
                 })}>
                   {!isDragOver && isSmallScreen ? (
                     <>
                       <ActionIcons
                         loading={loading}
                         disableOption={disableOption}
                         handleOpenEmojiModal={handleOpenEmojiModal}
                         handleMenuOpen={handleMenuOpen}
                       />

                      <CustomInput
                        loading={loading}
                        inputRef={inputRef}
                        ticketStatus={ticketStatus}
                        inputMessage={inputMessage}
                        setInputMessage={setInputMessage}
                        handleSendMessage={handleSendMessage}
                        handleInputPaste={handleInputPaste}
                        disableOption={disableOption}
                        quickMessages={memoizedQuickMessages}
                        handleAddQuickMessage={handleAddQuickMessage}
                      />

                      {canSendComposer ? (
                        <Button
                          onClick={handleSendMessage}
                          disabled={loading || disableOption()}
                          variant="containedPrimary"
                          className={classes.SendButton}
                        >
                          <SendIcon fill={theme.palette.text.main} />
                        </Button>
                      ) : (
                        <ActionButtons
                          loading={loading}
                          recording={recording}
                          ticketStatus={ticketStatus}
                          composerLocked={cloudComposerLocked}
                          handleSendMessage={handleSendMessage}
                          handleCancelAudio={handleCancelAudio}
                          handleUploadAudio={handleUploadAudio}
                          handleStartRecording={handleStartRecording}
                        />
                      )}

                      {showEmoji && (
                        <div className={classes.emojiBoxMobile}>
                          <Picker
                            perLine={8}
                            showPreview={false}
                            showSkinTones={false}
                            onSelect={(emoji) => {
                              handleAddEmoji(emoji);
                              setShowEmoji(false);
                              setEmojiModalOpen(false);
                              handleMenuClose();
                            }}
                            i18n={{
                              search: "Buscar",
                              clear: "Limpiar",
                              notfound: "No se encontraron emojis",
                              categories: {
                                search: "Resultados de búsqueda",
                                recent: "Recientes",
                                people: "Personas",
                                nature: "Naturaleza",
                                foods: "Comida",
                                activity: "Actividad",
                                places: "Lugares",
                                objects: "Objetos",
                                symbols: "Símbolos",
                                flags: "Banderas",
                                custom: "Personalizado"
                              }
                            }}
                          />
                        </div>
                      )}

                      {quickMessageModalOpen && (
                        <>
                          {/* Backdrop para capturar eventos */}
                          <div 
                            className={classes.quickMessagesBackdrop}
                            onMouseDown={() => setQuickMessageModalOpen(false)}
                            onWheel={(e) => e.stopPropagation()}
                            onMouseMove={(e) => e.stopPropagation()}
                          />
                          <div 
                            className={classes.quickMessagesBoxMobile}
                            onWheel={(e) => {
                              e.stopPropagation();
                              const target = e.currentTarget;
                              const { scrollTop, scrollHeight, clientHeight } = target;
                              if (scrollTop === 0 && e.deltaY < 0) {
                                e.preventDefault();
                              }
                              if (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0) {
                                e.preventDefault();
                              }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onMouseMove={(e) => e.stopPropagation()}
                          >
                            {quickMessagesData.map((item) => (
                              <div
                                key={item.id}
                                className={classes.quickMessageItem}
                                onClick={() => {
                                  handleAddQuickMessage(item);
                                  setQuickMessageModalOpen(false);
                                  handleMenuClose();
                                }}
                              >
                                {item.shortcode}: {truncateText(item.message, 94)}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  ) : !isDragOver && (
                    <>
                      <div
                        className={clsx(
                          classes.inputRow,
                          hasAttachmentPreview && classes.inputRowWithMedia,
                        )}
                      >
                        <AttachmentPreview
                          classes={classes}
                          medias={medias}
                          quickMessageMedia={quickMessageMedia}
                          previewUrl={previewUrl}
                          showMessengerSplitHint={showMessengerSplitHint}
                          messengerSplitHintKey={messengerSplitHintKey}
                          onRemove={handleRemoveAttachment}
                          formatFileSize={formatFileSize}
                          theme={theme}
                        />

                        <div className={classes.inputRowMain}>
                          <ActionIcons
                            loading={loading}
                            disableOption={disableOption}
                            handleOpenEmojiModal={handleOpenEmojiModal}
                            handleMenuOpen={handleMenuOpen}
                          />

                          <CustomInput
                            loading={loading}
                            inputRef={inputRef}
                            ticketStatus={ticketStatus}
                            inputMessage={inputMessage}
                            setInputMessage={setInputMessage}
                            handleSendMessage={handleSendMessage}
                            handleInputPaste={handleInputPaste}
                            disableOption={disableOption}
                            quickMessages={memoizedQuickMessages}
                            handleAddQuickMessage={handleAddQuickMessage}
                          />

                          {canSendComposer ? (
                            <Button
                              onClick={handleSendMessage}
                              disabled={loading || disableOption()}
                              variant="containedPrimary"
                              className={classes.SendButton}
                            >
                              <SendIcon fill={theme.palette.text.main} />
                            </Button>
                          ) : (
                            <ActionButtons
                              loading={loading}
                              recording={recording}
                              ticketStatus={ticketStatus}
                              composerLocked={cloudComposerLocked}
                              handleSendMessage={handleSendMessage}
                              handleCancelAudio={handleCancelAudio}
                              handleUploadAudio={handleUploadAudio}
                              handleStartRecording={handleStartRecording}
                            />
                          )}
                        </div>
                      </div>

                      {showEmoji && (
                        <div className={classes.emojiBox}>
                          <Picker
                            perLine={getEmojiPerLine(props.width)}
                            showPreview={false}
                            showSkinTones={false}
                            onSelect={(emoji) => {
                              handleAddEmoji(emoji);
                              setEmojiModalOpen(false);
                            }}
                            i18n={{
                              search: "Buscar",
                              clear: "Limpiar",
                              notfound: "No se encontraron emojis",
                              categories: {
                                search: "Resultados de búsqueda",
                                recent: "Recientes",
                                people: "Personas",
                                nature: "Naturaleza",
                                foods: "Comida",
                                activity: "Actividad",
                                places: "Lugares",
                                objects: "Objetos",
                                symbols: "Símbolos",
                                flags: "Banderas",
                                custom: "Personalizado"
                              }
                            }}
                          />
                        </div>
                      )}

                      {quickMessageModalOpen && (
                        <>
                          {/* Backdrop para capturar eventos */}
                          <div 
                            className={classes.quickMessagesBackdrop}
                            onMouseDown={() => setQuickMessageModalOpen(false)}
                            onWheel={(e) => e.stopPropagation()}
                            onMouseMove={(e) => e.stopPropagation()}
                          />
                          <div 
                            className={classes.quickMessagesBox}
                            onWheel={(e) => {
                              e.stopPropagation();
                              const target = e.currentTarget;
                              const { scrollTop, scrollHeight, clientHeight } = target;
                              if (scrollTop === 0 && e.deltaY < 0) {
                                e.preventDefault();
                              }
                              if (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0) {
                                e.preventDefault();
                              }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onMouseMove={(e) => e.stopPropagation()}
                          >
                            {quickMessagesData.map((item) => (
                              <div
                                key={item.id}
                                className={classes.quickMessageItem}
                                onClick={() => handleAddQuickMessage(item)}
                              >
                                {item.shortcode}: {truncateText(item.message, 94)}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </Paper>

            <Modal
              open={showPreview}
              onClose={() => !uploadingMedia && handleClosePreview()}
              className={classes.previewModal}
            >
              <div className={classes.previewModalContent}>
                <IconButton 
                  className={classes.closeButton}
                  onClick={() => {
                    handleClosePreview();
                    setMedias([]);
                  }}
                  disabled={uploadingMedia}
                >
                  <CancelIcon style={{ color: '#fff' }} />
                </IconButton>

                {previewUrl && medias[0]?.type.startsWith('image/') ? (
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className={classes.previewImage}
                  />
                ) : (
                  <div className={classes.filePreview}>
                    <DocumentTextIcon style={{ fontSize: 64, color: '#fff' }} />
                  </div>
                )}
                
                <div className={classes.previewActions}>
                  <div className={classes.previewInfo}>
                    <Typography className={classes.fileName}>
                      {medias[0]?.name}
                    </Typography>
                    <Typography className={classes.fileSize}>
                      {formatFileSize(medias[0]?.size || 0)}
                    </Typography>
                  </div>

                  <div className={classes.actionButtons}>
                    <Button
                      variant="contained"
                      className={classes.cancelButton}
                      onClick={() => {
                        handleClosePreview();
                        setMedias([]);
                      }}
                      disabled={uploadingMedia}
                      startIcon={<CancelIcon />}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      className={classes.sendButton}
                      onClick={async () => {
                        if (uploadingMedia) return;
                        setUploadingMedia(true);
                        setPercentLoading(0);
                        try {
                          await handleSendMessage();
                          handleClosePreview();
                        } catch (err) {
                          console.error(err);
                          toastError("Error al enviar el archivo");
                        } finally {
                          setUploadingMedia(false);
                        }
                      }}
                      disabled={uploadingMedia}
                      startIcon={uploadingMedia ? null : <SendIcon />}
                      style={{ 
                        opacity: uploadingMedia ? 0.5 : 1,
                        cursor: uploadingMedia ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {uploadingMedia ? 'Enviando...' : 'Enviar'}
                    </Button>
                  </div>
                </div>

                {uploadingMedia && (
                  <div style={{ 
                    width: '100%', 
                    marginTop: '40px',
                    position: 'absolute',
                    bottom: '20px',
                    left: 0,
                    padding: '0 20px'
                  }}>
                    <LinearWithValueLabel progress={percentLoading} />
                  </div>
                )}
              </div>
            </Modal>

            {/* Menú de opciones */}
            <Menu
              id="icon-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              disableAutoFocusItem
              disableRestoreFocus
              MenuListProps={{
                style: { padding: 0 },
                autoFocusItem: false,
              }}
              PaperProps={{
                style: {
                  minWidth: "300px",
                  width: isSmallScreen ? "100vw" : "auto",
                  maxWidth: isSmallScreen ? "100%" : "350px",
                  height: "auto",
                  maxHeight: "40vh",
                  backgroundColor: theme.palette.inputBackground,
                  display: "flex",
                  flexDirection: "column",
                  zIndex: 1300,
                  padding: 0,
                  marginLeft: isSmallScreen ? -15 : 0,
                  marginBottom: 15,
                  borderRadius: isSmallScreen ? 0 : 8,
                  color: theme.palette.text.secondary,
                },
              }}
            >
              {renderCloudTemplateMenuItem()}

              <MenuItem>
                  <FileInput
                    disableOption={disableOption}
                    handleChangeMedias={handleChangeMedias}
                    menu={true}
                  />
              </MenuItem>
              
              <MenuItem onClick={handleOpenQuickMessageModal}>
                <QuickMessages
                  disabled={disableOption()}
                  quickMessageModalOpen={false}
                  setQuickMessageModalOpen={setQuickMessageModalOpen}
                  menu={true}
                />
                <span style={{ marginLeft: 10 }}>Respuestas rápidas</span>
              </MenuItem>
              
              <MenuItem onClick={() => {
                setOpen(true);
                handleMenuClose();
              }}>
                <div style={{ display: 'flex', alignItems: 'center', width: '100%',  marginLeft: '12px'}}>
                  <CalendarIcon />
                  <span style={{ marginLeft: '22px', width: '24px', height: '24px' }} >Programar mensaje</span>
                </div>
              </MenuItem>
              
              <MenuItem
                onClick={() => {
                  setWhisperMode(prev => !prev);
                  handleMenuClose();
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginLeft: '11px', marginTop: '3px'}}>
                  <WhisperIcon 
                    style={{ 
                      marginRight: '17px',
                      color: whisperMode ? green[500] : 'inherit'
                    }}
                  />
                  {whisperMode ? "Desactivar modo susurro" : "Activar modo susurro"}
                </div>
              </MenuItem>
              
              <MenuItem onClick={handleMenuClose}>
                <SignSwitch
                  width={props.width}
                  setSignMessage={setSignMessage}
                  signMessage={signMessage}
                  menu={true}
                  small="small"
                  marginLeft={-1}
                />
              </MenuItem>
            </Menu>

            {/* Modal de programación de mensajes */}
            <ScheduleModal
              open={open}
              onClose={() => setOpen(false)}
              contactId={contactId}
            />
          </Paper>
        </div>
      );
        } else {
      return (
        <div className={classes.composerRoot}>
          {renderComposerChrome()}

          <Paper 
            variant="outlined" 
            className={clsx(classes.mainWrapper, {
              [classes.withReplyMessage]: !!replyingMessage
            })}
          >
            {replyingMessage && renderReplyingMessage(replyingMessage)}
            
            <Paper
              square
              elevation={0}
              className={clsx(classes.mainContainer, classes.scrollContainer, {
                [classes.mainContainerClosed]: disableOption(),
                [classes.expandedForDrag]: isDragOver,
                [classes.noScroll]: isDragOver
              })}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {/* Overlay de drag and drop */}
              {isDragOver && (
                <div className={classes.dragOverlay}>
                  <AttachFileIcon className={classes.dragOverlayIcon} />
                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <Typography className={classes.dragOverlayText}>
                      CARGAR ARCHIVOS
                    </Typography>
                    <Typography 
                      className={classes.dragOverlayText} 
                      style={{ 
                        fontSize: '20px', 
                        fontWeight: 600, 
                        marginTop: '12px',
                        opacity: 0.9 
                      }}
                    >
                      Suelta tu archivo aquí
                    </Typography>
                  </div>
                </div>
              )}
             
{ticketStatus === "open" && (
                 <div className={clsx(classes.newMessageBox, {
                   [classes.expandedForDrag]: isDragOver
                 })}>
                   {!isDragOver && isSmallScreen ? (
                     <>
                       <ActionIcons
                         loading={loading}
                         disableOption={disableOption}
                         handleOpenEmojiModal={handleOpenEmojiModal}
                         handleMenuOpen={handleMenuOpen}
                       />

                      <CustomInput
                        loading={loading}
                        inputRef={inputRef}
                        ticketStatus={ticketStatus}
                        inputMessage={inputMessage}
                        setInputMessage={setInputMessage}
                        handleSendMessage={handleSendMessage}
                        handleInputPaste={handleInputPaste}
                        disableOption={disableOption}
                        quickMessages={memoizedQuickMessages}
                        handleAddQuickMessage={handleAddQuickMessage}
                      />

                      {canSendComposer ? (
                        <Button
                          onClick={handleSendMessage}
                          disabled={loading || disableOption()}
                          variant="containedPrimary"
                          className={classes.SendButton}
                        >
                          <SendIcon fill={theme.palette.text.main} />
                        </Button>
                      ) : (
                        <ActionButtons
                          loading={loading}
                          recording={recording}
                          ticketStatus={ticketStatus}
                          composerLocked={cloudComposerLocked}
                          handleSendMessage={handleSendMessage}
                          handleCancelAudio={handleCancelAudio}
                          handleUploadAudio={handleUploadAudio}
                          handleStartRecording={handleStartRecording}
                        />
                      )}
                    </>
                  ) : !isDragOver && (
                    <>
                      <div
                        className={clsx(
                          classes.inputRow,
                          hasAttachmentPreview && classes.inputRowWithMedia,
                        )}
                      >
                        <AttachmentPreview
                          classes={classes}
                          medias={medias}
                          quickMessageMedia={quickMessageMedia}
                          previewUrl={previewUrl}
                          showMessengerSplitHint={showMessengerSplitHint}
                          messengerSplitHintKey={messengerSplitHintKey}
                          onRemove={handleRemoveAttachment}
                          formatFileSize={formatFileSize}
                          theme={theme}
                        />

                        <div className={classes.inputRowMain}>
                          <ActionIcons
                            loading={loading}
                            disableOption={disableOption}
                            handleOpenEmojiModal={handleOpenEmojiModal}
                            handleMenuOpen={handleMenuOpen}
                          />

                          <CustomInput
                            loading={loading}
                            inputRef={inputRef}
                            ticketStatus={ticketStatus}
                            inputMessage={inputMessage}
                            setInputMessage={setInputMessage}
                            handleSendMessage={handleSendMessage}
                            handleInputPaste={handleInputPaste}
                            disableOption={disableOption}
                            quickMessages={memoizedQuickMessages}
                            handleAddQuickMessage={handleAddQuickMessage}
                          />

                          {canSendComposer ? (
                            <Button
                              onClick={handleSendMessage}
                              disabled={loading || disableOption()}
                              variant="containedPrimary"
                              className={classes.SendButton}
                            >
                              <SendIcon fill={theme.palette.text.main} />
                            </Button>
                          ) : (
                            <ActionButtons
                              loading={loading}
                              recording={recording}
                              ticketStatus={ticketStatus}
                              composerLocked={cloudComposerLocked}
                              handleSendMessage={handleSendMessage}
                              handleCancelAudio={handleCancelAudio}
                              handleUploadAudio={handleUploadAudio}
                              handleStartRecording={handleStartRecording}
                            />
                          )}
                        </div>
                      </div>

                      {showEmoji && (
                        <div className={classes.emojiBox}>
                          <Picker
                            perLine={getEmojiPerLine(props.width)}
                            showPreview={false}
                            showSkinTones={false}
                            onSelect={handleAddEmoji}
                            i18n={{
                              search: "Buscar",
                              clear: "Limpiar",
                              notfound: "No se encontraron emojis",
                              categories: {
                                search: "Resultados de búsqueda",
                                recent: "Recientes",
                                people: "Personas",
                                nature: "Naturaleza",
                                foods: "Comida",
                                activity: "Actividad",
                                places: "Lugares",
                                objects: "Objetos",
                                symbols: "Símbolos",
                                flags: "Banderas",
                                custom: "Personalizado"
                              }
                            }}
                          />
                        </div>
                      )}

                      {quickMessageModalOpen && (
                        <>
                          {/* Backdrop para capturar eventos */}
                          <div 
                            className={classes.quickMessagesBackdrop}
                            onMouseDown={() => setQuickMessageModalOpen(false)}
                            onWheel={(e) => e.stopPropagation()}
                            onMouseMove={(e) => e.stopPropagation()}
                          />
                          <div 
                            className={classes.quickMessagesBox}
                            onWheel={(e) => {
                              e.stopPropagation();
                              const target = e.currentTarget;
                              const { scrollTop, scrollHeight, clientHeight } = target;
                              if (scrollTop === 0 && e.deltaY < 0) {
                                e.preventDefault();
                              }
                              if (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0) {
                                e.preventDefault();
                              }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onMouseMove={(e) => e.stopPropagation()}
                          >
                            {quickMessagesData.map((item) => (
                              <div
                                key={item.id}
                                className={classes.quickMessageItem}
                                onClick={() => handleAddQuickMessage(item)}
                              >
                                {item.shortcode}: {truncateText(item.message, 94)}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </Paper>

            {/* Menú de opciones */}
            <Menu
              id="icon-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              disableAutoFocusItem
              disableRestoreFocus
              MenuListProps={{
                style: { padding: 0 },
                autoFocusItem: false,
              }}
              PaperProps={{
                style: {
                  minWidth: "300px",
                  width: isSmallScreen ? "100vw" : "auto",
                  maxWidth: isSmallScreen ? "100%" : "350px",
                  height: "auto",
                  maxHeight: "40vh",
                  backgroundColor: theme.palette.inputBackground,
                  display: "flex",
                  flexDirection: "column",
                  zIndex: 1300,
                  padding: 0,
                  marginLeft: isSmallScreen ? -15 : 0,
                  marginBottom: 15,
                  borderRadius: isSmallScreen ? 0 : 8,
                  color: theme.palette.text.secondary,
                },
              }}
            >
              {renderCloudTemplateMenuItem()}

              <MenuItem>
                  <FileInput
                    disableOption={disableOption}
                    handleChangeMedias={handleChangeMedias}
                    menu={true}
                  />
              </MenuItem>
              
              <MenuItem onClick={handleOpenQuickMessageModal}>
                <QuickMessages
                  disabled={disableOption()}
                  quickMessageModalOpen={false}
                  setQuickMessageModalOpen={setQuickMessageModalOpen}
                  menu={true}
                />
                <span style={{ marginLeft: 10 }}>Respuestas rápidas</span>
              </MenuItem>
              
              <MenuItem
                onClick={() => {
                  setOpen(true);
                  handleMenuClose();
                }}
                disabled={channel === 'webchat'}
                >
                <div style={{ display: 'flex', alignItems: 'center', width: '100%',  marginLeft: '12px'}}>
                  <CalendarIcon />
                  <span style={{ marginLeft: '22px', width: '24px', height: '24px' }} >Programar mensaje</span>
                </div>
              </MenuItem>
              
              <MenuItem
                onClick={() => {
                  setWhisperMode(prev => !prev);
                  handleMenuClose();
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginLeft: '11px', marginTop: '3px'}}>
                  <WhisperIcon 
                    style={{ 
                      marginRight: '17px',
                      color: whisperMode ? green[500] : 'inherit'
                    }}
                  />
                  {whisperMode ? "Desactivar modo susurro" : "Activar modo susurro"}
                </div>
              </MenuItem>
              
              <MenuItem onClick={handleMenuClose}>
                <SignSwitch
                  width={props.width}
                  setSignMessage={setSignMessage}
                  signMessage={signMessage}
                  menu={true}
                  small="small"
                  marginLeft={-1}
                />
              </MenuItem>
            </Menu>
          </Paper>
        </div>
      );
    }
  };

  // Renderizar también el modal de programación
  return (
    <>
      {renderMainComponent()}
      
      {/* Modal de programación */}
      <ScheduleModal
        open={open}
        onClose={() => setOpen(false)}
        contactId={contactId}
        whatsappId={whatsappId}
        ticketId={ticketId}
      />

      <WhatsappTemplatePickerModal
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        whatsappId={whatsappId}
        ticketId={ticketId}
        contactId={contactId}
        onOptimisticMessage={onOptimisticMessage}
        senderName={user?.name}
      />

      <MessengerTagSendModal
        open={messengerTagModalOpen}
        onClose={() => setMessengerTagModalOpen(false)}
        ticketId={ticketId}
        channel={channel}
        onOptimisticMessage={onOptimisticMessage}
        senderName={user?.name}
        signMessage={signMessage}
      />
    </>
  );
};

export default MessageInputCustom;